/* My profile — your photo, your details, your email and your password.
 *
 * Until this existed the only way to change a name or a birthday was to ask an
 * administrator, and there was no way at all to change a password short of
 * "Forgot password".
 *
 * THE MOBILE IS NOT HERE TO EDIT. It is how people sign in and what every
 * invitation and check-in matches on; changing it needs a code sent to the
 * new number, which is the church administrator's to arrange.
 *
 * A NEW EMAIL IS UNCONFIRMED until its code is entered — the same step as at
 * signup — so a typo cannot become the address a reset is sent to.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { AlertCircle, Camera, CheckCircle2, Loader2 } from 'lucide-react'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Field, SelectField } from '@/components/ui/field'
import { api, ApiError, tokens } from '@/api/client'
import { useMe, useUploadImage } from '@/api/queries'
import type { Me, Session } from '@/types/api'

type Profile = Me & {
  email_verified?: boolean
  birthday?: string | null
  gender?: string | null
  has_password?: boolean
}

function messageOf(err: unknown, fallback: string) {
  return err instanceof ApiError ? err.message : fallback
}

function Notice({ tone, children }: { tone: 'ok' | 'error'; children: React.ReactNode }) {
  return tone === 'ok' ? (
    <p className="flex items-center gap-1 text-base text-ink-2">
      <CheckCircle2 className="h-4 w-4" aria-hidden />
      {children}
    </p>
  ) : (
    <div
      role="alert"
      className="flex items-start gap-s2 rounded border border-danger/30 bg-danger-soft p-s3 text-base text-danger"
    >
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <span>{children}</span>
    </div>
  )
}

export function ProfilePage() {
  const { data } = useMe()
  const me = data as Profile | undefined
  const qc = useQueryClient()

  function saved(next: Profile) {
    qc.setQueryData(['me'], next)
  }

  if (!me) {
    return (
      <Card className="p-s6 text-center text-ink-2">
        <Loader2 className="mx-auto h-5 w-5 animate-spin" aria-label="Loading" />
      </Card>
    )
  }

  return (
    <div className="space-y-s4">
      <header>
        <h1 className="text-lg font-extrabold tracking-tight">My profile</h1>
        <p className="text-base text-ink-2">
          How you appear to your church, and how you sign in.
        </p>
      </header>
      <PhotoCard me={me} onSaved={saved} />
      <DetailsCard me={me} onSaved={saved} />
      <EmailCard me={me} onSaved={saved} />
      <PasswordCard me={me} />
    </div>
  )
}

/* ------------------------------------------------------------------ photo */

function PhotoCard({ me, onSaved }: { me: Profile; onSaved: (m: Profile) => void }) {
  const input = useRef<HTMLInputElement>(null)
  const upload = useUploadImage()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const name = [me.first_name, me.last_name].filter(Boolean).join(' ')

  async function choose(file: File | undefined) {
    if (!file) return
    setBusy(true)
    setError(null)
    try {
      const media = await upload.mutateAsync(file)
      onSaved(await api.patch<Profile>('/auth/me', { avatar_media_id: media.id }))
    } catch (err) {
      setError(messageOf(err, 'That photo could not be used. Try another.'))
    } finally {
      setBusy(false)
      if (input.current) input.current.value = ''
    }
  }

  async function remove() {
    setBusy(true)
    setError(null)
    try {
      onSaved(await api.patch<Profile>('/auth/me', { remove_avatar: true }))
    } catch (err) {
      setError(messageOf(err, 'Could not remove your photo.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card className="flex flex-wrap items-center gap-s4 p-s4">
      <div className="relative">
        <Avatar name={name} src={me.avatar_url} size="xl" />
        {busy ? (
          <span className="absolute inset-0 grid place-items-center rounded-pill bg-black/40">
            <Loader2 className="h-5 w-5 animate-spin text-white" aria-label="Uploading" />
          </span>
        ) : null}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-md font-bold">{name}</p>
        <p className="text-sm text-ink-2">
          {me.church_name ? `${me.church_name} · ` : ''}
          {me.mobile}
        </p>
        {error ? <p className="mt-1 text-sm text-danger">{error}</p> : null}
      </div>
      <div className="flex gap-s2">
        <input
          ref={input}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => choose(e.target.files?.[0])}
        />
        <Button variant="secondary" disabled={busy} onClick={() => input.current?.click()}>
          <Camera className="h-4 w-4" aria-hidden />
          {me.avatar_url ? 'Change photo' : 'Add photo'}
        </Button>
        {me.avatar_url ? (
          <Button variant="secondary" disabled={busy} onClick={remove}>
            Remove
          </Button>
        ) : null}
      </div>
    </Card>
  )
}

/* ---------------------------------------------------------------- details */

function DetailsCard({ me, onSaved }: { me: Profile; onSaved: (m: Profile) => void }) {
  const [first, setFirst] = useState(me.first_name)
  const [last, setLast] = useState(me.last_name ?? '')
  const [birthday, setBirthday] = useState(me.birthday ?? '')
  const [gender, setGender] = useState(me.gender ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  useEffect(() => setDone(false), [first, last, birthday, gender])

  async function save(e: FormEvent) {
    e.preventDefault()
    if (!first.trim()) {
      setError('Your first name is needed.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      onSaved(
        await api.patch<Profile>('/auth/me', {
          first_name: first.trim(),
          last_name: last.trim() || null,
          birthday: birthday || null,
          gender: gender || null,
        }),
      )
      setDone(true)
    } catch (err) {
      setError(messageOf(err, 'Could not save your details.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card className="p-s4">
      <form onSubmit={save} className="space-y-s4">
        <h2 className="text-md font-bold">Personal details</h2>
        <div className="grid gap-s3 sm:grid-cols-2">
          <Field label="First name" value={first} onChange={(e) => setFirst(e.target.value)} />
          <Field label="Last name" value={last} onChange={(e) => setLast(e.target.value)} />
          <Field
            label="Birthday"
            type="date"
            value={birthday}
            max={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setBirthday(e.target.value)}
          />
          <SelectField
            label="Gender"
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            options={[
              { value: '', label: 'Not set' },
              { value: 'male', label: 'Male' },
              { value: 'female', label: 'Female' },
            ]}
          />
        </div>
        <Field
          label="Mobile"
          value={me.mobile ?? ''}
          disabled
          help="You sign in with this number. To change it, ask your church administrator."
        />
        {error ? <Notice tone="error">{error}</Notice> : null}
        <div className="flex items-center justify-end gap-s3">
          {done ? <Notice tone="ok">Saved</Notice> : null}
          <Button type="submit" variant="primary" loading={busy}>
            Save details
          </Button>
        </div>
      </form>
    </Card>
  )
}

/* ------------------------------------------------------------------ email */

type EmailChallenge = { request_id: string; ttl_s: number; dev_code?: string | null }

function EmailCard({ me, onSaved }: { me: Profile; onSaved: (m: Profile) => void }) {
  const [email, setEmail] = useState(me.email ?? '')
  const [challenge, setChallenge] = useState<EmailChallenge | null>(null)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const changed = email.trim().toLowerCase() !== (me.email ?? '').toLowerCase()
  const unconfirmed = Boolean(me.email) && me.email_verified === false

  async function sendCode() {
    const sent = await api.post<EmailChallenge>('/auth/email/send-code', {})
    setChallenge(sent)
    setCode(sent.dev_code ?? '')
  }

  async function save(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      if (changed) {
        onSaved(await api.patch<Profile>('/auth/me', { email: email.trim() }))
      }
      await sendCode()
    } catch (err) {
      setError(messageOf(err, 'Could not change your email.'))
    } finally {
      setBusy(false)
    }
  }

  async function confirm(e: FormEvent) {
    e.preventDefault()
    if (!challenge) return
    setBusy(true)
    setError(null)
    try {
      await api.post('/auth/email/verify', {
        request_id: challenge.request_id,
        code: code.trim(),
      })
      setChallenge(null)
      onSaved({ ...me, email: email.trim().toLowerCase(), email_verified: true })
    } catch (err) {
      setError(messageOf(err, 'That code is not right.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card className="p-s4">
      <h2 className="text-md font-bold">Email</h2>
      {challenge ? (
        <form onSubmit={confirm} className="mt-s3 space-y-s3">
          <p className="text-base text-ink-2">
            We sent a code to {email.trim()}. Enter it to confirm the address.
          </p>
          <Field
            label="Code"
            value={code}
            inputMode="numeric"
            onChange={(e) => setCode(e.target.value)}
            help={challenge.dev_code ? `Test mode — your code is ${challenge.dev_code}` : undefined}
          />
          {error ? <Notice tone="error">{error}</Notice> : null}
          <div className="flex justify-end gap-s2">
            <Button type="button" variant="secondary" onClick={() => setChallenge(null)}>
              Later
            </Button>
            <Button type="submit" variant="primary" loading={busy} disabled={!code.trim()}>
              Confirm
            </Button>
          </div>
        </form>
      ) : (
        <form onSubmit={save} className="mt-s3 space-y-s3">
          <Field
            label="Email address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            help={
              changed
                ? 'You will be asked for a code sent to the new address.'
                : unconfirmed
                  ? 'Not confirmed yet.'
                  : me.email
                    ? 'Confirmed.'
                    : undefined
            }
          />
          {error ? <Notice tone="error">{error}</Notice> : null}
          <div className="flex justify-end">
            {changed || unconfirmed ? (
              <Button
                type="submit"
                variant="primary"
                loading={busy}
                disabled={!email.trim()}
              >
                {changed ? 'Change email' : 'Confirm email'}
              </Button>
            ) : null}
          </div>
        </form>
      )}
    </Card>
  )
}

/* --------------------------------------------------------------- password */

function PasswordCard({ me }: { me: Profile }) {
  const hasPassword = me.has_password !== false
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [again, setAgain] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  async function save(e: FormEvent) {
    e.preventDefault()
    setDone(false)
    if (next.length < 8) {
      setError('Use at least 8 characters.')
      return
    }
    if (next !== again) {
      setError('The two new passwords do not match.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const session = await api.post<Session>('/auth/password', {
        ...(hasPassword ? { current_password: current } : {}),
        new_password: next,
      })
      // Every other session is signed out, including the token this page
      // was using — carry on with the fresh one.
      tokens.save(session)
      setCurrent('')
      setNext('')
      setAgain('')
      setDone(true)
    } catch (err) {
      setError(messageOf(err, 'Could not change your password.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card className="p-s4">
      <form onSubmit={save} className="space-y-s3">
        <h2 className="text-md font-bold">{hasPassword ? 'Password' : 'Set a password'}</h2>
        <p className="text-base text-ink-2">
          {hasPassword
            ? 'Changing it signs you out on your other devices.'
            : 'So you can sign in with your mobile or email and a password.'}
        </p>
        <div className="grid gap-s3 sm:grid-cols-3">
          {hasPassword ? (
            <Field
              label="Current password"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          ) : null}
          <Field
            label="New password"
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
          <Field
            label="New password again"
            type="password"
            autoComplete="new-password"
            value={again}
            onChange={(e) => setAgain(e.target.value)}
          />
        </div>
        {error ? <Notice tone="error">{error}</Notice> : null}
        <div className="flex items-center justify-end gap-s3">
          {done ? (
            <Notice tone="ok">Password changed. Other devices are signed out.</Notice>
          ) : null}
          <Button
            type="submit"
            variant="primary"
            loading={busy}
            disabled={(hasPassword && !current) || !next || !again}
          >
            {hasPassword ? 'Change password' : 'Set password'}
          </Button>
        </div>
      </form>
    </Card>
  )
}
